import { useEffect, useState } from "react";
import DeskCloseX from "@/components/common/DeskCloseX";
import { AlertCircle } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { LOT_TYPE_ICON, publishLotTypes } from "./crmShared";
import { CAT_THEME, CAT_ORDER, catTheme, M, Star } from "./typeTheme";

/** Icônes proposées (spec 2.3, extensible) : home, home-2, bed, building, package, store, car + le reste du catalogue. */
const ICONS = ["Home", "Home2", "Bed", "Building", "Package", "Store", "Car", "Warehouse", "TreePine", "Building2", "Layers", "Square"];

export function IconPicker({ value, onChange, testId }) {
  return (
    <div className="flex flex-wrap" style={{ gap: 5 }} data-testid={testId}>
      {ICONS.map((k) => { const I = LOT_TYPE_ICON[k]; const on = value === k; return (
        <button key={k} type="button" onClick={() => onChange(k)} title={k} className="inline-flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 6, background: on ? "rgba(91,95,199,.2)" : "#0f0f12", border: `0.5px solid ${on ? "#5B5FC7" : "rgba(255,255,255,.1)"}`, color: on ? "#a3a6ec" : "#a4a4ae", cursor: "pointer" }} aria-pressed={on} data-testid={`${testId}-${k}`}><I size={14} /></button>); })}
    </div>
  );
}

/** Sélecteur segmenté 4 colonnes « Catégorie d'habitabilité » avec icône (spec 2.3). */
export function CategoryGrid({ value, onChange, testId, disabled }) {
  return (
    <div className="grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 5 }} role="radiogroup" data-testid={testId}>
      {CAT_ORDER.map((k) => { const t = CAT_THEME[k]; const on = value === k; return (
        <button key={k} type="button" role="radio" aria-checked={on} disabled={disabled} onClick={() => onChange(k)} className="flex flex-col items-center" style={{ gap: 4, padding: 8, borderRadius: 7, background: on ? t.bg : "#0f0f12", border: `0.5px solid ${on ? t.color : "rgba(255,255,255,.1)"}`, color: on ? t.light : "#a4a4ae", fontSize: 11, cursor: disabled ? "default" : "pointer" }} data-testid={`${testId}-${k}`}>
          <t.Icon size={14} /><span>{t.label}</span>
        </button>); })}
    </div>
  );
}

/** Champ « Nom du type » avec erreur 409 inline (rouge 10 px + alert-circle, bord rouge). */
export function NameField({ value, onChange, error, testId, autoFocus }) {
  return (
    <div>
      <div style={M.label}>Nom du type <Star /></div>
      <input value={value} autoFocus={autoFocus} onChange={(e) => onChange(e.target.value)} placeholder="ex. Studio, Loft, F6…" style={{ ...M.input, ...(error ? { borderColor: "#DC2626" } : {}) }} aria-invalid={!!error} data-testid={testId} />
      {error && <div className="flex items-center" style={{ gap: 4, marginTop: 5, fontSize: 10, color: "#DC2626" }} data-testid={`${testId}-error`}><AlertCircle size={10} />{error}</div>}
    </div>
  );
}

/** Modale « Modifier le type » (crayon) : nom, icône, catégorie — les champs de saisie restent dans « Configuration du type ». */
export default function LotTypeEditDialog({ type, onClose, onSaved }) {
  const [label, setLabel] = useState(type.label);
  const [icon, setIcon] = useState(type.icon || "Square");
  const [category, setCategory] = useState(type.category || "habitable");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { const h = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } }; window.addEventListener("keydown", h, true); return () => window.removeEventListener("keydown", h, true); }, [onClose]);
  const th = catTheme(category);
  const Icon = LOT_TYPE_ICON[icon] || LOT_TYPE_ICON.Square;
  const disabled = busy || !label.trim() || !!err;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/crm/lot-types/${type.key}`, { label: label.trim(), icon, category });
      publishLotTypes(data.items); toast.success(`Type « ${label.trim()} » modifié`); onSaved?.(data.item); onClose();
    } catch (e) { const d = e?.response?.data?.detail || "Erreur"; if ([400, 409].includes(e?.response?.status)) setErr(d); else toast.error(d); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 flex items-center justify-center" style={M.overlay} onClick={onClose} data-desk-modal="" data-testid="lot-type-edit-dialog">
      <div onClick={(e) => e.stopPropagation()} style={M.box}>
        <div className="flex items-center" style={M.head}>
          <span className="inline-flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 8, background: th.icon35 }}><Icon size={16} style={{ color: "#fff" }} /></span>
          <div style={{ flex: 1 }}><div style={M.title}>Modifier le type</div><div style={M.sub}>S'applique à tous les lots de ce type</div></div>
          <DeskCloseX onClick={onClose} testId="lot-type-edit-close" />
        </div>
        <div style={M.body}>
          <NameField value={label} onChange={(v) => { setLabel(v); setErr(""); }} error={err} testId="lot-type-edit-name" autoFocus />
          <div><div style={M.label}>Icône</div><IconPicker value={icon} onChange={setIcon} testId="lot-type-edit-icon" /></div>
          <div><div style={M.label}>Catégorie d'habitabilité <Star /></div><CategoryGrid value={category} onChange={setCategory} testId="lot-type-edit-category" /></div>
        </div>
        <div className="flex items-center justify-end" style={M.foot}>
          <button type="button" onClick={onClose} style={M.btnGhost} data-testid="lot-type-edit-cancel">Annuler</button>
          <button type="button" onClick={save} disabled={disabled} style={{ ...M.btnPrimary, opacity: disabled ? 0.45 : 1, cursor: disabled ? "not-allowed" : "pointer" }} data-testid="lot-type-edit-save">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}
